import { Link } from 'react-router-dom';
import {
  LEP_DO_ID,
  MISSION_CONTROL_METRICS,
  CI_CD_PIPELINE_STAGES,
  INFRASTRUCTURE_CAPABILITIES,
  RELEASE_STRATEGIES,
  ENVIRONMENT_TYPES,
  FEATURE_FLAG_CAPABILITIES,
  OBSERVABILITY_SIGNALS,
  SRE_METRICS,
  INCIDENT_PHASES,
  AUTO_HEALING_ACTIONS,
  CAPACITY_PLANNING_AREAS,
  SECURITY_OPS_CAPABILITIES,
  DISASTER_RECOVERY,
  AI_OPS_COPILOT,
  DEVOPS_ANALYTICS,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_MISSION_KPIS,
  DEMO_PIPELINES,
  DEMO_INFRASTRUCTURE,
  DEMO_RELEASES,
  DEMO_OBSERVABILITY,
  DEMO_SRE,
  DEMO_AUTO_HEALING,
  DEMO_PRODUCTION_OPS,
  buildDevOpsDashboard,
} from '../../enterprisedevops/framework';
import {
  MissionControlPanel,
  CICDPipelinesPanel,
  InfrastructurePanel,
  ReleaseManagementPanel,
  ObservabilityPanel,
  SREIncidentPanel,
  AutoHealingPanel,
  ProductionOperationsPanel,
} from '../../components/enterprisedevops';
import { Card, KpiCard } from '../../components/ui';

export function EnterpriseDevOpsPage() {
  const dashboard = buildDevOpsDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{LEP_DO_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise DevOps Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Enterprise DevOps, CI/CD, Observability, SRE & Production Operations — the operational backbone
          of the AI Commerce Operating System.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-100 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Mission Control Metrics" value={dashboard.mission_control_metrics.toString()} />
        <KpiCard label="Pipeline Stages" value={dashboard.pipeline_stages.toString()} />
        <KpiCard label="Infrastructure Caps" value={dashboard.infrastructure_capabilities.toString()} />
        <KpiCard label="SRE Metrics" value={dashboard.sre_metrics.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Mission Control</h2>
        <p className="mt-1 text-sm text-slate-500">{MISSION_CONTROL_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <MissionControlPanel kpis={DEMO_MISSION_KPIS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">CI/CD Pipelines</h2>
          <p className="mt-1 text-sm text-slate-500">{CI_CD_PIPELINE_STAGES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <CICDPipelinesPanel runs={DEMO_PIPELINES} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Infrastructure</h2>
          <p className="mt-1 text-sm text-slate-500">{INFRASTRUCTURE_CAPABILITIES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <InfrastructurePanel resources={DEMO_INFRASTRUCTURE} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Release Management</h2>
        <p className="mt-1 text-sm text-slate-500">{RELEASE_STRATEGIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ReleaseManagementPanel releases={DEMO_RELEASES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Observability</h2>
        <p className="mt-1 text-sm text-slate-500">{OBSERVABILITY_SIGNALS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ObservabilityPanel streams={DEMO_OBSERVABILITY} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">SRE & Incident Management</h2>
          <p className="mt-1 text-sm text-slate-500">{SRE_METRICS.join(' · ')} · {INCIDENT_PHASES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <SREIncidentPanel metrics={DEMO_SRE} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Auto Healing</h2>
          <p className="mt-1 text-sm text-slate-500">{AUTO_HEALING_ACTIONS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <AutoHealingPanel actions={DEMO_AUTO_HEALING} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Production Operations</h2>
        <p className="mt-1 text-sm text-slate-500">{SECURITY_OPS_CAPABILITIES.join(' · ')} · {AI_OPS_COPILOT.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ProductionOperationsPanel items={DEMO_PRODUCTION_OPS} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Environment Management</h2>
          <p className="mt-2 text-xs text-slate-500">{ENVIRONMENT_TYPES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Feature Flags</h2>
          <p className="mt-2 text-xs text-slate-500">{FEATURE_FLAG_CAPABILITIES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Capacity Planning</h2>
          <p className="mt-2 text-xs text-slate-500">{CAPACITY_PLANNING_AREAS.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Backup & Disaster Recovery</h2>
          <p className="mt-2 text-xs text-slate-500">{DISASTER_RECOVERY.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">DORA Analytics</h2>
          <p className="mt-2 text-xs text-slate-500">{DEVOPS_ANALYTICS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
